import { useMemo, useState } from "react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, QueryStateBoundary } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { useInvoices } from "@/api/hooks/useInvoices";
import {
  INVOICE_KIND_BADGE_VARIANT,
  INVOICE_KIND_LABELS,
  INVOICE_STATUS_BADGE_VARIANT,
  INVOICE_STATUS_LABELS,
  paidCents,
  type Invoice,
} from "@/api/types/invoice";
import { RecordPaymentDialog } from "@/features/money/RecordPaymentDialog";

/**
 * The event's deposit/balance/addon invoices with a quick "Record payment"
 * action. Restored as its own component during the FoxCRM Phase 1 scaffold:
 * PourCRM's EventMoneySection.tsx bundled this (core invoicing — explicitly
 * kept) together with the mobile-bar event-cost/contribution-margin editor
 * (explicitly stripped) in one file. Deleting the whole file during the
 * strip took this out too as collateral damage; caught via a live Playwright
 * pass against money.spec.ts and split back out here with only the
 * invoices portion. Sits alongside (not in place of) MoneyCard, which
 * tracks the event's own deposit_paid_at/balance_paid_at markers via a
 * separate mechanism.
 */
export function EventInvoicesCard({ eventId }: { eventId: string }) {
  const { data, isLoading, error, refetch } = useInvoices({ event_id: eventId });
  const invoices = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const [payInvoiceId, setPayInvoiceId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
      </CardHeader>
      <CardContent>
        <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
          {invoices.length === 0 ? (
            <p className="text-sm text-text-muted" data-testid="event-invoices-empty">
              No invoices yet.
            </p>
          ) : (
            <ul className="divide-y divide-border" data-testid="event-invoices-list">
              {invoices.map((invoice) => (
                <EventInvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  onPay={() => setPayInvoiceId(invoice.id)}
                />
              ))}
            </ul>
          )}
        </QueryStateBoundary>
      </CardContent>

      {payInvoiceId && (
        <RecordPaymentDialog
          open={!!payInvoiceId}
          onClose={() => setPayInvoiceId(null)}
          invoiceId={payInvoiceId}
        />
      )}
    </Card>
  );
}

function EventInvoiceRow({ invoice, onPay }: { invoice: Invoice; onPay: () => void }) {
  const canPay = invoice.status !== "void" && invoice.status !== "paid";

  return (
    <li
      className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-invoice-id={invoice.id}
    >
      <div>
        <p className="font-medium text-text-primary">
          {invoice.number}
          <Badge variant={INVOICE_KIND_BADGE_VARIANT[invoice.kind]} size="sm" className="ml-2">
            {INVOICE_KIND_LABELS[invoice.kind]}
          </Badge>
        </p>
        <p className="text-xs text-text-muted">
          {centsToUsd(invoice.total_cents)} total · {centsToUsd(paidCents(invoice))} paid
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={INVOICE_STATUS_BADGE_VARIANT[invoice.status]} data-testid={`event-invoice-status-${invoice.id}`}>
          {INVOICE_STATUS_LABELS[invoice.status]}
        </Badge>
        {canPay && (
          <Button size="sm" onClick={onPay}>
            Record payment
          </Button>
        )}
        <a
          href={`/money/${invoice.id}`}
          className="text-sm font-medium text-primary hover:underline"
        >
          View
        </a>
      </div>
    </li>
  );
}
